import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const Popup = styled.div`
  position: fixed;
  z-index: 1000;
  background: #d8e5ed;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  min-width: 240px;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(8px)')};
  transition: opacity 0.22s ease, transform 0.22s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -7px;
    left: 20px;
    width: 14px;
    height: 14px;
    background: #d8e5ed;
    transform: rotate(45deg);
    border-radius: 2px;
  }
`;

const getDayHours = (hours) => {
  if (hours) {
    const hoursSplit = hours.split('-');
    if (hoursSplit.length > 0 && hoursSplit[0].length > 1) return hours;
  }
  return '';
};

const hasHoursOrDisplayText = (day) =>
  day && (day.displayText || getDayHours(day.hours));

const HoursPopup = ({ open, anchorRef, schedule = [], onClose }) => {
  const popupRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, bottom: 0 });

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      left: rect.left,
      bottom: window.innerHeight - rect.top + 8,
    });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return;
    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        anchorRef.current?.contains(e.target) ||
        popupRef.current?.contains(e.target)
      ) {
        return;
      }
      onClose();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose, anchorRef]);

  return (
    <Popup
      ref={popupRef}
      $open={open}
      style={{ left: pos.left, bottom: pos.bottom }}
      role='dialog'
      aria-label='Sales center hours'
      aria-hidden={!open}
    >
      <div
        className='font-graphie text-mattamy-blue text-sm'
        style={{
          display: 'grid',
          gridTemplateColumns: '52px 1fr',
          rowGap: 8,
          columnGap: 16,
        }}
      >
        {schedule.map((entry) => {
          if (!entry || !entry.day) return null;
          if (!hasHoursOrDisplayText(entry)) return null;
          const dayShort = entry.day.replace(':', '').substring(0, 3);
          return (
            <Fragment key={entry.day}>
              <span className='font-semibold'>{dayShort}</span>
              <div className='flex flex-col'>
                <span>{getDayHours(entry.hours)}</span>
                <span>{entry.displayText}</span>
              </div>
            </Fragment>
          );
        })}
      </div>
    </Popup>
  );
};

HoursPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  schedule: PropTypes.array,
  onClose: PropTypes.func.isRequired,
};

export default HoursPopup;
